import { FC, memo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Mail } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';

import { AUTH_ROUTES } from '@navigation/types';
import { useAlert } from '@providers/AlertProvider';
import { useGoogleAuthMutation } from '@redux/queries/auth';
import { isErrorWithStringMessage, ErrorType } from '@redux/baseQuery';
import { AlertType } from '@utils/constants';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import xIcon from '@assets/icons/x.png';
import googleIcon from '@assets/icons/google.png';
// import appleIcon from '@assets/icons/apple.png';

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  margin-block: 12px;
`;

const AuthButton = styled(TouchableOpacity)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 50%;
  border: 0.5px solid ${({ theme }) => theme.colors.grey[700]};
  background: ${({ theme }) => theme.colors.grey[700]};
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: transparent;
    border: 0.5px solid ${({ theme }) => theme.colors.grey[400]};
    transform: translateY(-2px);
  }
`;

const Icon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const InvertedIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
  filter: invert(100%);
`;

const MailIcon = styled(Mail)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.white};
`;

const DEFAULT_ERROR_MESSAGE = 'Login failed';

const AuthOptions: FC = () => {
  const navigate = useNavigate();
  const [loginWithGoogle, { isLoading, isError, error }] =
    useGoogleAuthMutation();
  const { showAlert } = useAlert();

  const onLoginWithEmail = useCallback(() => {
    navigate(AUTH_ROUTES.LOGIN);
  }, [navigate]);

  const onLoginWithX = useCallback(() => {
    window.location.href = `${process.env.REACT_APP_API_URL}/api/v1/auth/x?platform=web`;
  }, []);

  const onLoginWithGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      loginWithGoogle(codeResponse.code);
    },
    onError: () => {
      showAlert(AlertType.Error, DEFAULT_ERROR_MESSAGE);
    },
    flow: 'auth-code',
  });

  // const onLoginWithApple = useCallback(() => {
  // TODO: Implement login with Apple
  // }, []);

  useEffect(() => {
    if (isError) {
      showAlert(
        AlertType.Error,
        isErrorWithStringMessage(error)
          ? (error as ErrorType)?.data?.message || DEFAULT_ERROR_MESSAGE
          : DEFAULT_ERROR_MESSAGE,
      );
    }
  }, [isError, showAlert, error]);

  return (
    <OptionsContainer>
      <AuthButton onClick={onLoginWithX}>
        <InvertedIcon src={xIcon} />
      </AuthButton>

      <AuthButton onClick={onLoginWithGoogle} disabled={isLoading}>
        <Icon src={googleIcon} />
      </AuthButton>

      {/* <AuthButton onClick={onLoginWithApple}>
        <Icon src={appleIcon} />
      </AuthButton> */}

      <AuthButton onClick={onLoginWithEmail} disabled={isLoading}>
        <MailIcon />
      </AuthButton>
    </OptionsContainer>
  );
};

export default memo(AuthOptions);
